import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { SetInitialStateFacade } from '../../../../../common/models';
import { LocalDateRangeModel } from '../../../../models';
import { HubContextFacade } from '../../../administration/hub/context/hub-context.facade';
import { HubBrief } from '../../../administration/hub/models/hub.model';
import { CatalogSearchCriteriaFacade, CatalogSearchCriteriaType } from '../../../catalog-search-criteria';
import { AllocationCalendarSlot, AllocationViewCalendarSearchCriteria } from '../models';
import { AllocationCalendarDockModel } from '../models/allocation-view-calendar';
import { convertTimeFrameFilterToUTC } from '../utils';
import {
  AllocationViewCalendarInitialState,
  AllocationViewCalendarLoad,
  AllocationViewCalendarSelectSlot,
  AllocationViewCalendarUpdateDocksAfterDrop,
} from './allocation-view-calendar.actions';
import { AllocationViewCalendarState } from './allocation-view-calendar.state';

@Injectable({ providedIn: 'root' })
export class AllocationViewCalendarFacade implements SetInitialStateFacade {
  calendarData$: Observable<AllocationCalendarDockModel[]> = this.store.select(
    AllocationViewCalendarState.getCalendarData,
  );
  selectedSlot$: Observable<AllocationCalendarSlot> = this.store.select(AllocationViewCalendarState.getSelectedSlot);
  loaded$: Observable<boolean> = this.store.select(AllocationViewCalendarState.getLoaded);

  constructor(
    private catalogSearchCriteriaFacade: CatalogSearchCriteriaFacade,
    private hubContextFacade: HubContextFacade,
    private store: Store,
  ) {}

  loadAll(): void {
    this.hubContextFacade.selectedHub$
      .pipe(
        filter(Boolean),
        switchMap((hub: HubBrief) =>
          combineLatest([
            of(hub),
            this.catalogSearchCriteriaFacade.getSearchCriteriaByCatalogType(CatalogSearchCriteriaType.AllocationView),
            this.hubContextFacade.contextTimezone$,
          ]),
        ),
        take(1),
      )
      .subscribe(
        ([hubBrief, allocationViewSearchCriteria, contextTimezone]: [
          HubBrief,
          AllocationViewCalendarSearchCriteria,
          string,
        ]) => {
          const { id, timezone } = hubBrief;
          const timeFrame =
            contextTimezone === timezone
              ? convertTimeFrameFilterToUTC(allocationViewSearchCriteria.timeFrame, timezone)
              : convertTimeFrameFilterToUTC(allocationViewSearchCriteria.timeFrame, contextTimezone);
          this.store.dispatch(
            new AllocationViewCalendarLoad({
              ...allocationViewSearchCriteria,
              timeFrame,
              hubId: id,
            }),
          );
        },
      );
  }

  updateDocksAfterDrop(
    slotId: number,
    originalDockId: number,
    allocationDockId: number,
    timeRange: LocalDateRangeModel,
  ): void {
    this.store.dispatch(
      new AllocationViewCalendarUpdateDocksAfterDrop({ slotId, originalDockId, allocationDockId, timeRange }),
    );
  }

  selectSlot(slot: AllocationCalendarSlot): void {
    this.store.dispatch(new AllocationViewCalendarSelectSlot(slot));
  }

  setInitialState(): void {
    this.store.dispatch(new AllocationViewCalendarInitialState());
  }
}
